// Field Executive dashboard (client, 2026-10-07) — phone-first: today's visits,
// follow-ups due, this month's sales against target, invoice requests and
// quick actions. Only the executive's own work; nothing company-wide.
import { Link } from 'react-router-dom';
import {
  AlarmClock, CalendarCheck, CheckCircle2, ChevronRight, Download, FileText, FlaskConical, MapPin, Navigation,
  Plus, Target, UserPlus, Users,
} from 'lucide-react';
import { ROUTES } from '@/routes/paths';
import { Spinner } from '@/shared/components/ui/Spinner';
import { inr, invoiceDownloadUrl, STATUS, useFieldDashboard, type FieldFollowup } from '../api';

const time = (d: string) => new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
const date = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
const followupLink = (f: FieldFollowup) => {
  if (!f.parentId) return ROUTES.dashboard;
  if (['lead', 'deal', 'enquiry'].includes(f.parentType || '')) return `/pipeline/${f.parentId}`;
  if (f.parentType === 'sample') return `/sampling?sample=${f.parentId}`;
  return ROUTES.dashboard;
};

export function FieldDashboardPage() {
  const { data, isLoading, error } = useFieldDashboard();
  if (isLoading) return <div className="flex justify-center py-24"><Spinner /></div>;
  if (error || !data) return <p className="m-4 rounded-xl bg-danger/10 p-4 text-sm text-danger-text">Could not load your dashboard. Pull down to refresh.</p>;
  const m = data.month;
  const pct = m.achievementPct;
  const first = data.user.name.split(/\s+/)[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const pending = data.counts.requests.requested || 0;

  return (
    <div className="mx-auto max-w-3xl space-y-4 pb-24">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 p-5 text-white shadow-xs">
        <p className="text-sm text-success-text">{greeting},</p>
        <h1 className="text-2xl font-bold">{first}</h1>
        <p className="mt-1 text-xs text-success-text">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <Stat label="Visits today" value={String(data.visitsToday.length)} />
          <Stat label="Follow-ups due" value={String(data.followups.today.length + data.followups.overdue.length)} />
          <Stat label="Requests pending" value={String(pending)} />
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        <Quick to={ROUTES.visits} icon={MapPin} label="Visits" />
        <Quick to={ROUTES.pipelineNew} icon={Plus} label="New lead" />
        <Quick to={`${ROUTES.contacts}?new=1`} icon={UserPlus} label="Add contact" />
        <Quick to={`${ROUTES.fieldInvoiceRequests}?new=1`} icon={FileText} label="Invoice request" />
        <Quick to={ROUTES.samplingNew} icon={FlaskConical} label="Request sample" />
        <Quick to={ROUTES.contacts} icon={Users} label={`My contacts (${data.counts.contacts})`} />
      </div>

      {/* Today's visits */}
      <Card title="Today's visits" icon={Navigation} action={<Link to={ROUTES.visits} className="text-xs font-semibold text-success-text">Open planner</Link>}>
        {data.visitsToday.length === 0 ? (
          <p className="py-4 text-center text-sm text-on-surface-variant">No visits planned today. <Link to={ROUTES.visits} className="font-semibold text-success-text">Plan one</Link></p>
        ) : (
          <ul className="divide-y divide-border">
            {data.visitsToday.map((v) => (
              <li key={v.id} className="flex items-center gap-3 py-3">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${v.status === 'completed' ? 'bg-success/10 text-success-text' : v.status === 'checked_in' ? 'bg-info/10 text-info-text' : 'bg-muted text-on-surface-variant'}`}>
                  {v.status === 'completed' ? <CheckCircle2 className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-on-surface">{v.name}</p>
                  <p className="truncate text-xs text-on-surface-variant">{time(v.plannedAt)} · {v.location}</p>
                </div>
                <Link to={ROUTES.visits} className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold ${v.status === 'planned' ? 'bg-success/10 text-white' : v.status === 'checked_in' ? 'bg-info/10 text-white' : 'bg-muted text-on-surface-variant'}`}>
                  {v.status === 'planned' ? 'Check in' : v.status === 'checked_in' ? 'Check out' : 'Done'}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Follow-ups */}
      <Card title="Follow-ups" icon={AlarmClock}>
        {data.followups.overdue.length + data.followups.today.length === 0 ? (
          <p className="py-4 text-center text-sm text-on-surface-variant">Nothing due. 👍</p>
        ) : (
          <ul className="divide-y divide-border">
            {[...data.followups.overdue.map((f) => ({ ...f, late: true })), ...data.followups.today.map((f) => ({ ...f, late: false }))].slice(0, 10).map((f) => (
              <li key={f.id}>
                <Link to={followupLink(f)} className="flex items-center gap-3 py-2.5">
                  <span className={`rounded-md px-1.5 py-0.5 text-data-xs font-bold ${f.late ? 'bg-danger/10 text-danger-text' : 'bg-warning/10 text-warning-text'}`}>{f.late ? `Overdue · ${date(f.date)}` : time(f.date)}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-on-surface-variant">{f.note || 'Follow-up'}</span>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* This month */}
      <Card title="This month" icon={Target}>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><p className="text-xs text-on-surface-variant">My sales</p><p className="text-lg font-bold text-on-surface">{inr(m.sales)}</p></div>
          <div><p className="text-xs text-on-surface-variant">Target</p><p className="text-lg font-bold text-on-surface">{m.target ? inr(m.target) : '—'}</p></div>
          <div><p className="text-xs text-on-surface-variant">Visits done</p><p className="text-lg font-bold text-on-surface">{m.visitsCompleted}</p></div>
        </div>
        {pct !== null && (
          <div className="mt-3">
            <div className="mb-1 flex justify-between text-xs"><span className="text-on-surface-variant">Achievement</span><b className={pct >= 100 ? 'text-success-text' : pct >= 80 ? 'text-warning-text' : 'text-danger-text'}>{pct}%</b></div>
            <div className="h-2 rounded-full bg-muted"><div className={`h-2 rounded-full ${pct >= 100 ? 'bg-success/10' : pct >= 80 ? 'bg-warning/10' : 'bg-danger/10'}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
          </div>
        )}
        {!m.target && <p className="mt-2 text-center text-xs text-on-surface-subtle">No target set for this month yet.</p>}
      </Card>

      {/* Invoice requests */}
      <Card title="My invoice requests" icon={FileText} action={<Link to={ROUTES.fieldInvoiceRequests} className="text-xs font-semibold text-success-text">See all</Link>}>
        {data.recentRequests.length === 0 ? (
          <p className="py-4 text-center text-sm text-on-surface-variant">No requests yet. <Link to={`${ROUTES.fieldInvoiceRequests}?new=1`} className="font-semibold text-success-text">Raise one</Link></p>
        ) : (
          <ul className="divide-y divide-border">
            {data.recentRequests.map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-on-surface">{r.clientName}</p>
                  <p className="text-xs text-on-surface-variant">{r.number} · {inr(r.total)}</p>
                  {r.status === 'rejected' && r.rejectReason && <p className="mt-0.5 text-xs text-danger-text">“{r.rejectReason}”</p>}
                </div>
                {r.status === 'sent' ? (
                  <a href={invoiceDownloadUrl(r.id)} className="inline-flex items-center gap-1 rounded-lg bg-success/10 px-2.5 py-1.5 text-xs font-semibold text-white"><Download className="h-3.5 w-3.5" /> Invoice</a>
                ) : (
                  <span className={`rounded-full px-2 py-0.5 text-data-xs font-semibold ring-1 ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p className="flex items-center justify-center gap-1 text-data-xs text-on-surface-subtle"><CalendarCheck className="h-3 w-3" /> Open leads: {data.counts.openLeads}</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl bg-card/15 px-3 py-2 text-center"><p className="text-xl font-bold">{value}</p><p className="text-data-xs text-emerald-50">{label}</p></div>;
}

function Quick({ to, icon: Icon, label }: { to: string; icon: typeof MapPin; label: string }) {
  return (
    <Link to={to} className="flex flex-col items-center gap-1.5 rounded-card border border-border bg-card p-3 text-center shadow-xs transition active:scale-95 hover:border-success/30">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-success/10 text-success-text"><Icon className="h-5 w-5" /></span>
      <span className="text-data-xs font-semibold leading-tight text-on-surface-variant">{label}</span>
    </Link>
  );
}

function Card({ title, icon: Icon, action, children }: { title: string; icon: typeof MapPin; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-border bg-card p-4 shadow-xs">
      <header className="mb-1 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-bold text-on-surface"><Icon className="h-4 w-4 text-success-text" />{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}

export default FieldDashboardPage;
